import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'rectangular' | 'circular';
  width?: string | number;
  height?: string | number;
  count?: number;
}

const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'rectangular',
  width,
  height,
  count = 1
}) => {
  const baseClasses = 'animate-pulse bg-slate-200';

  const variantClasses = {
    text: 'rounded',
    rectangular: 'rounded-lg',
    circular: 'rounded-full'
  };

  const style: React.CSSProperties = {
    width: width || '100%',
    height: height || (variant === 'text' ? '1em' : '100%')
  };

  const elements = Array(count).fill(0).map((_, i) => (
    <div
      key={i}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  ));

  return count === 1 ? elements[0] : <>{elements}</>;
};

// Article Card Skeleton
export const ArticleCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl overflow-hidden border border-slate-100">
    <Skeleton className="aspect-[16/10]" />
    <div className="p-4 space-y-3">
      <Skeleton variant="text" width="30%" height="12px" />
      <Skeleton variant="text" height="20px" />
      <Skeleton variant="text" height="20px" width="80%" />
      <div className="flex items-center gap-2 pt-2">
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="text" width="40%" height="12px" />
      </div>
    </div>
  </div>
);

// Article Page Skeleton
export const ArticlePageSkeleton: React.FC = () => (
  <article className="max-w-3xl mx-auto px-4 py-8">
    {/* Breadcrumb */}
    <div className="flex gap-2 mb-6">
      <Skeleton variant="text" width={50} height={14} />
      <Skeleton variant="text" width={80} height={14} />
    </div>

    {/* Header */}
    <div className="mb-8 space-y-4">
      <Skeleton variant="text" width={80} height={16} />
      <Skeleton variant="text" height={36} />
      <Skeleton variant="text" height={36} width="70%" />
      <Skeleton variant="text" height={20} width="90%" className="mt-4" />
      <div className="flex items-center gap-3 pt-2">
        <Skeleton variant="text" width={100} height={14} />
        <Skeleton variant="text" width={80} height={14} />
        <Skeleton variant="text" width={60} height={14} />
      </div>
    </div>

    {/* Featured Image */}
    <Skeleton className="aspect-[16/9] mb-8 -mx-4 md:mx-0" />

    {/* Content */}
    <div className="space-y-4">
      <Skeleton variant="text" height={18} />
      <Skeleton variant="text" height={18} />
      <Skeleton variant="text" height={18} width="85%" />
      <Skeleton variant="text" height={18} />
      <Skeleton variant="text" height={18} width="90%" />
      <Skeleton variant="text" height={28} width="60%" className="mt-8" />
      <Skeleton variant="text" height={18} />
      <Skeleton variant="text" height={18} />
      <Skeleton variant="text" height={18} width="75%" />
    </div>
  </article>
);

// Tools Grid Skeleton
export const ToolsGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array(count).fill(0).map((_, i) => (
      <div key={i} className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-xl">
        <Skeleton variant="rectangular" width={32} height={32} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" height={18} width="70%" />
          <Skeleton variant="text" height={14} width="90%" />
        </div>
      </div>
    ))}
  </div>
);

export default Skeleton;
